import type { EmbedContent } from "@babackapp/private-api-client/generated/schema";

const DEV_EMBED_URL = "http://localhost:5175/index.js";

export function injectEmbedScript(
  embedData: EmbedContent,
  shopId: string,
  { noOverlayClose = true }: { noOverlayClose?: boolean } = {},
) {
  if (document.querySelector("script[data-shop-id]")) return;

  // VITE_USE_LEGACY_WIDGET skips the local dev server (localhost:5175, always
  // the new widget) so the isNewReturnWidgetEnabled flag can be tested locally —
  // off for a shop means the API returns the legacy widget's script instead.
  const useNewWidgetDevServer =
    import.meta.env.DEV && import.meta.env.VITE_USE_LEGACY_WIDGET !== "true";
  const embedSrc = useNewWidgetDevServer ? DEV_EMBED_URL : embedData.embedScriptUrl;

  if (!embedSrc) {
    // Legacy widget: `window.baback.isOpen` is only read once, at the widget's
    // initial mount (see apps/return-widget/src/index.tsx checkIsOpen) — setting
    // it afterwards does nothing. Reopening an already-initialized widget for
    // the same shop means calling its exposed openPopup() directly instead of
    // re-running the bootstrap script (which would re-append its CDN <script> tags).
    const legacyBaback = (window as unknown as Record<string, unknown>).baback as
      | { providerId?: string; openPopup?: () => void }
      | undefined;
    if (legacyBaback?.providerId === shopId) {
      legacyBaback.openPopup?.();
      return;
    }

    const scriptFn = new Function(embedData.script);
    scriptFn();
    return;
  }

  const s = document.createElement("script");
  s.src = embedSrc;
  s.setAttribute("data-shop-id", shopId);
  s.setAttribute("data-base", window.location.origin);
  s.setAttribute("data-auto-open", "true");
  s.setAttribute("data-position", embedData.widgetPosition ?? "center");
  s.setAttribute("data-advanced", "true");
  if (noOverlayClose) s.setAttribute("data-no-overlay-close", "true");
  s.async = true;
  document.head.appendChild(s);
}
